<template>
  <div class="preview-wrapper"
    v-loading.fullscreen="isRequesting"
    element-loading-text="サーバーに問い合わせています">

    <template v-if="isInitialized">
      <div :class="['preview', previewSize]">
        <div class="page">

          <template v-for="type in ['navigation', 'sidebar']">
            <template v-if="themes[type] && themes[type].id">
              <div :is="getComponentNameById(themes[type].id)" :bus="bus"
                :component="themes[type]"
                :preview-size="previewSize" :lock="true"></div>
            </template>
          </template>

          <el-form class="form">
            <template v-if="page.header">
              <div :is="getComponentNameById(header.component.id)" :bus="bus"
                :component="header.component"
                :preview-size="previewSize"
                v-on:update="updateComponent('header', $event)"
                v-on:delete="deleteComponent('header')"></div>
            </template>

            <template v-for="(component, index) in page.components">
              <div class="component-wrapper">

                <div :is="getComponentNameById(component.id)" :bus="bus"
                  :index="index" :positionMax="page.components.length"
                  :component="component"
                  :components="page.components"
                  :preview-size="previewSize"
                  v-on:update="updateComponent(index, $event)"
                  v-on:delete="deleteComponent(index)"></div>

                <template v-if="page.components.length - 1 > index">
                  <div class="component-swap flex justify-center items-center"  @click="swapComponents(index)"><el-tooltip class="item" effect="dark" content="Swap component" placement="top-start">
                      <i class="fa fa-arrows-v" aria-hidden="true"></i>
                    </el-tooltip>
                  </div>
                </template>
              </div>
            </template>
          </el-form>

          <template v-if="page.components.length === 0">
            <div class="component-warning flex justify-center items-center" @click="dialogTemplatesVisible = true">
              <span class="component-warning-span">ここをクリックしてページテンプレートを選択するか、左下のボタンからコンポーネントを追加してコンテンツを編集してください</span>
            </div>
          </template>


          <template v-for="type in ['footer']">
            <template v-if="themes[type]">
              <div :is="getComponentNameById(themes[type].id)" :bus="bus"
                :component="themes[type]"
                :preview-size="previewSize" :lock="true"></div>
            </template>
          </template>

        </div>
      </div>
    </template>

    <el-dialog class="select-components-dialog flex flex-column" width="75%" title="コンポーネントの選択" :visible.sync="dialogComponentsVisible">
      <select-component @select="selectComponent($event)"></select-component>
    </el-dialog>

    <el-dialog width="75%" title="テンプレートの選択" :visible.sync="dialogTemplatesVisible">
      <el-carousel :autoplay="false" type="card" height="400px">
        <template v-for="(template, i) in appTemplates">
          <el-carousel-item :key="template.id">
            <div class="template-wrapper flex column justify-center items-center" @click="selectTemplate(i)">
              <div class="template-content">
                <h3 class="template-h3">{{ template.meta.title }}</h3>
                <p class="template-p">{{ template.meta.description }}</p>
              </div>
              <img class="template-img" :src="screenshot('templates', template.id)">
            </div>
          </el-carousel-item>
        </template>
      </el-carousel>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogTemplatesVisible = false">キャンセル</el-button>
      </span>
    </el-dialog>

    <fab
      position="bottom-left"
      bg-color="#20a0ff"
      icon-size="small"
      main-icon="settings"
      :actions="fabActions"
      @native="previewSize = 'native'"
      @smartphone="previewSize = 'smartphone'"
      @tablet="previewSize = 'tablet'"
      @desktop="previewSize = 'desktop'"
      @launch="window.open(page.link, '_blank')"
      @add="addComponent"
      @sync="onSubmit($event)"></fab>

    <el-dialog width="50%" :title="error.title" :visible.sync="dialogErrorVisible">
      <span>{{ error.message }}</span>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="dialogErrorVisible = false">OK</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import union from 'lodash/union'
import RouteBase from '@/components/mixins/RouteBase'
import SelectComponent from '@/components/app/SelectComponent'
import cloneDeep from 'lodash/cloneDeep'
import FAB from 'vue-fab'
import AppAPI from '@/api'

export default {
  name: 'Page',
  mixins: [RouteBase],
  components: {
    fab: FAB,
    'select-component': SelectComponent
  },
  props: ['id'],
  data () {
    return {
      window: window,
      bus: new Vue(),
      appTemplates: AppAPI.templatesArray.filter(template => template.meta.type === 'driving').map(template => {
        /**
         * componentsは
         *   "components": [4003, 4004, 4002]
         * のようにIDを含む配列か
         *   "components": [{"id": 4003, ...}]
         * のようにカスタムcontentsが設定されたオブジェクトのどちらかになっている
         * 前者の場合はコンポーネントのデフォルトのcontentsを使用する
         */
        const components = template.components.map(component => {
          if (typeof component === 'number') {
            return {...AppAPI.components[component]}
          } else {
            let matched = {...AppAPI.components[component.id]}
            matched.contents = {...component.contents}
            return matched
          }
        })
        return {id: template.id, meta: template.meta, components}
      }),
      previewSize: 'native',
      dialogComponentsVisible: false,
      dialogTemplatesVisible: false
    }
  },
  computed: {
    fabActions: function () {
      return [
        {name: 'native', icon: 'devices', tooltip: 'ディスプレイサイズでプレビュー'},
        {name: 'smartphone', icon: 'smartphone', tooltip: 'スマホサイズでプレビュー'},
        {name: 'tablet', icon: 'tablet', tooltip: 'タブレットサイズでプレビュー'},
        {name: 'desktop', icon: 'desktop_mac', tooltip: 'デスクトップサイズでプレビュー'},
        {name: 'add', icon: 'add', tooltip: 'コンポーネント追加'},
        {name: 'sync', icon: 'sync', tooltip: '変更を保存'}
      ]
    }
  },
  methods: {
    /**
     * スクリーンショットアセットの取得
     */
    screenshot: function (type, id) {
      return require(`@/assets/${type}/${id}.png`)
    },
    /**
     * 保存時の処理
     */
    onSubmit: async function (ev) {
      /**
       * コンポーネントの入れ替え等で this._uid が変更されるため、
       * サーバと同期する直前に最新の this._uid ですべてのコンポーネントの HTML/CSS を生成する
       * このときにアンカーリンクも最新の this._uid を指すようにしておく
       */
      this.bus.$emit('update-child')

      this.page.html = this.page.components.map(component => component.html).reduce((p, c) => p + c, '')

      this.page.css = {
        layout: this.page.components.map(component => component.css).reduce((p, c) => !~p.indexOf(c.layout) ? p + c.layout : p, '') + (this.page.header && this.page.header.css ? this.page.header.css.layout : ''),
        theme: this.page.components.map(component => component.css).reduce((p, c) => !~p.indexOf(c.theme) ? p + c.theme : p, '') + (this.page.header && this.page.header.css ? this.page.header.css.theme : '')
      }

      if (this.page.components && this.page.components.length) {
        this.page.extensions = union(...this.page.components.map(component => component.extensions), this.page.header && this.page.header.extensions ? this.page.header.extensions : [])
      }

      this.themes.css.layout = require(`!css-to-string-loader!css-loader?minimize!sass-loader!@/scss/style.scss`)
      this.themes.css.theme = this.getCssForThemeColors() + `body {background-color:${this.themes.colors.base.hex}}`

      this.isRequesting = true
      let pageResponse = await this.$store.dispatch('wordpress/pages/update', this.page)
      let themeResponse = await this.$store.dispatch('wordpress/themes/update', this.themes)
      this.isRequesting = false

      if (pageResponse.id && JSON.stringify(themeResponse) === JSON.stringify(this.themes)) {
        this.$notify({
          title: '更新完了',
          message: 'サーバーデータを更新しました',
          type: 'success',
          duration: 1500
        })
      } else {
        this.$notify({
          title: '更新失敗',
          message: 'サーバーデータを更新できませんでした',
          type: 'error',
          duration: 1500
        })
      }
    },

    /**
     * コンポーネント追加時にコンポーネント選択ダイアログを表示
     */
    addComponent: function () {
      this.dialogComponentsVisible = true
    },

    /**
     * テンプレート追加時にテンプレート選択ダイアログを表示
     */
    selectTemplate: function (index) {
      this.page.components = cloneDeep(this.appTemplates[index].components)

      this.dialogTemplatesVisible = false
    },

    /**
     * コンポーネント編集後にコンポーネントを入れ替える
     */
    updateComponent: function (index, updated) {
      if (index + 1 === updated.newPosition) return
      this.page.components.splice(updated.newPosition - 1, 0, this.page.components.splice(index, 1)[0])
    },

    /**
     * コンポーネントを削除
     */
    deleteComponent: function (index) {
      this.page.components.splice(index, 1)
    },

    /**
     * コンポーネント選択ダイアログでコンポーネントを選択したときの処理
     */
    selectComponent: function (component) {
      if (process.env.NODE_ENV !== 'production') console.log(component)
      this.page.components ? this.page.components.push(component) : this.page.components = [component]
      this.dialogComponentsVisible = false
    },

    /**
     * コンポーネントの入れ替え
     */
    swapComponents: function (index) {
      this.page.components[index] = this.page.components.splice(index + 1, 1, this.page.components[index])[0]
    }
  },
  created: async function () {
    if (process.env.NODE_ENV !== 'production') console.log('On Page created...')

    this.isRequesting = true
    /**
     * navigationやfooterでpagesが必要になる
     * navigation/sidebar/footerコンポーネントが生成される前に
     * pagesを用意しておかないとrenderingできないため、requestThemesよりも
     * 前にpagesを取得しておく必要がある
     */
    try { await this.requestMedia() } catch (error) { return false }
    try { await this.requestPages() } catch (error) { return false }
    try { await this.requestSettings() } catch (error) { return false }
    try { await this.requestThemes() } catch (error) { return false }

    this.$store.commit('wordpress/pages/select', this.id)

    /**
     * 初期状態ではcomponentsは空文字としてWP APIから返ってくる
     * このアプリ側で一つでもコンポーネントが追加されると配列になる
     */
    if (!this.page.components) {
      this.page.components = []
    } else if (typeof this.page.components === 'string') {
      this.page.components = []
    } else {
      if (this.page.components.length !== this.page.components.filter(component => component.id).length) this.page.components = []
    }

    this.isRequesting = false

    this.injectThemeColorsStyle()

    this.isInitialized = true
  },
  destroyed: function () {
    if (process.env.NODE_ENV !== 'production') console.log('Deselect page on Page destroyed...')
    this.$store.commit('wordpress/pages/deselect')
  }
}
</script>

<style scoped lang="scss">
@import '~scss/variables';

.component {
  &-wrapper {
    position: relative;
  }
  &-swap {
    margin: auto;
    position: absolute;
    height: 1.5em;
    left: 0;
    right: 0;
    bottom: -.75em;
    z-index: 1;
    line-height: 1;
    color: #20a0ff;
    font-size: 2em;
    opacity: 0;
    &:hover {
      opacity: 1;
    }
  }
}

.template {
  &-wrapper {
    border: 1px solid #f3f3f3;
    min-height: 360px;
    background-color: #f3f3f3;
    -webkit-box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
            box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
    cursor: pointer;
    &:hover {
      -webkit-box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
              box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
      transition: all 300ms;
    }
  }
  &-content {
    padding: 1em;
  }
  &-h3 {
    text-align: center;
  }
  &-p {
    text-align: justify;
    margin-bottom: 0;
  }
  &-img {
    width: 100%;
  }
}
</style>

<readme>
# this.page
}
```
{
  "id": number,
  "date": string,
  "date_gmt": string,
  "guid": {
    "rendered": string
  },
  "modified": string,
  "modified_gmt": string,
  "slug": string,
  "status": string,
  "type": string,
  "link": string,
  "title": {
    "rendered": string
  },
  "content": {
    "rendered": string,
    "protected": boolean
  },
  "excerpt": {
    "rendered": string,
    "protected": boolean
  },
  "author": number,
  "featured_media": number,
  "parent": number,
  "menu_order": number,
  "comment_status": string,
  "ping_status": string,
  "template": string,
  "meta": [],
  header: {
    id: number,
    html: string,
    css: string,
    meta: {...},
    contents: {...}
  },
  components: [
    {
      id: number,
      html: string,
      css: string,
      meta: {...},
      contents: {...}
    }
  ],
  css: {
    layout: string,
    theme: string
  },
  extensions: Array<string>
  "_links": any
}
```

</readme>


// WEBPACK FOOTER //
// src/components/routes/Page.vue